/**
 * BrandedLockup -- the route-level Suspense fallback (#2580, Tier 5 "moments &
 * identity", EPIC #2575).
 *
 * Replaces the bare full-page spinner with a minimal branded lockup: the agency
 * logo (or, absent a logo, the agency NAME) from BrandingContext above a thin
 * indeterminate accent bar. This is the ONLY place the lockup appears -- in-page
 * loads stay skeletons (the Tier 1 convention).
 *
 * White-label (the #680/#695 rule): the name comes from BrandingContext, whose
 * neutral default is "CRM" -- never a hardcoded agency or platform-vendor name.
 * A branding-fetch failure keeps that neutral default, so the lockup never
 * leaks another agency; the white-label vendor-name guard stays green.
 *
 * Token-driven + dark-mode aware (brand tokens). `prefers-reduced-motion`: the
 * indeterminate bar stops sliding and shows a static filled segment (handled in
 * index.css `.crm-indeterminate-bar`) -- an unmistakable "loading" affordance
 * with NO movement. Agency-neutral, emoji-free, zero new npm dependency.
 */
import { useBranding } from "../contexts/branding";
import AgencyLogo from "./AgencyLogo";
import { agencyLogoSrc } from "../utils/agencyLogo";

export default function BrandedLockup() {
  const branding = useBranding();
  // Neutral fallback: BrandingContext's default agency_name is "CRM"; a fetch
  // failure keeps it. NEVER hardcode an agency name here.
  const name = branding.agency_name || "CRM";

  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center gap-5 bg-[var(--color-bg-page)] px-6"
      role="status"
      aria-live="polite"
      aria-label={`Loading ${name}`}
    >
      <div className="flex flex-col items-center gap-3">
        {agencyLogoSrc(branding, "letterhead") ? (
          // `surface="adaptive"`: `--color-bg-page` is light in light mode and
          // DARK in dark mode, so the light tile appears only under `dark:`
          // — dark agency artwork stays legible either way (#3417).
          <AgencyLogo variant="letterhead" surface="adaptive" />
        ) : (
          <span className="text-2xl font-semibold tracking-tight text-[var(--color-text-primary)]">
            {name}
          </span>
        )}
      </div>
      {/* Thin indeterminate accent bar. The track is faint; a small block
          slides across it (motion-safe -- static filled segment under
          reduced-motion). */}
      <div className="h-1 w-40 overflow-hidden rounded-full bg-[var(--color-sand)]">
        <div className="crm-indeterminate-bar h-full w-1/3 rounded-full bg-[var(--color-accent)]" />
      </div>
      <span className="sr-only">Loading</span>
    </div>
  );
}
